import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useSidebarData() {
  const { user } = useAuth();
  const [userOrganization, setUserOrganization] = useState<string | null>(null);
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  const [teamCode, setTeamCode] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  const [openTodosCount, setOpenTodosCount] = useState(0);
  const [activeAreaHotLeadsCount, setActiveAreaHotLeadsCount] = useState(0);
  const [unreadEmailsCount, setUnreadEmailsCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUserOrganization();
      fetchOpenTodosCount();
      fetchActiveAreaHotLeadsCount();
      fetchUnreadEmailsCount();
    }
  }, [user]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user) return;

    const organizationChannel = supabase
      .channel('organization-logo-changes-shared')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'organizations'
      }, async () => {
        await fetchUserOrganization();
      })
      .subscribe();

    const todosChannel = supabase
      .channel('todos-sidebar-changes-shared')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'todos',
        filter: `user_id=eq.${user.id}`
      }, async () => {
        await fetchOpenTodosCount();
      })
      .subscribe();

    const unitsChannel = supabase
      .channel('d2d-units-sidebar-changes-shared')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'd2d_units',
        filter: `user_id=eq.${user.id}`
      }, async () => {
        await fetchActiveAreaHotLeadsCount();
      })
      .subscribe();

    const areasChannel = supabase
      .channel('d2d-areas-sidebar-changes-shared')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'd2d_areas',
        filter: `created_by=eq.${user.id}`
      }, async () => {
        await fetchActiveAreaHotLeadsCount();
      })
      .subscribe();

    const emailsChannel = supabase
      .channel('emails-sidebar-changes-shared')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'emails',
        filter: `user_id=eq.${user.id}`
      }, async () => {
        await fetchUnreadEmailsCount();
      })
      .subscribe();

    const handleLogoUpdate = (event: CustomEvent) => {
      setOrganizationLogo(event.detail.logoUrl);
    };
    window.addEventListener('organizationLogoUpdated', handleLogoUpdate as any);

    return () => {
      supabase.removeChannel(organizationChannel);
      supabase.removeChannel(todosChannel);
      supabase.removeChannel(unitsChannel);
      supabase.removeChannel(areasChannel);
      supabase.removeChannel(emailsChannel);
      window.removeEventListener('organizationLogoUpdated', handleLogoUpdate as any);
    };
  }, [user]);

  const fetchUserOrganization = async () => {
    if (!user) return;
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select(`
          organization_id,
          team_id,
          organizations (name, logo_url),
          teams (team_code)
        `)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      const hasAdminRole = roles?.some(r => r.role === 'admin') || false;

      if (profile?.team_id) {
        const { data: teamData } = await supabase
          .from('teams')
          .select('team_leader_id')
          .eq('id', profile.team_id)
          .single();

        const isLeader = teamData?.team_leader_id === user.id;
        setIsTeamLeader(isLeader);
      }

      if (profile?.organizations) {
        const org = profile.organizations as any;
        const team = profile.teams as any;
        setUserOrganization(org.name);
        setTeamCode(team?.team_code || null);
        setIsAdmin(hasAdminRole);

        if (org.logo_url) {
          const { data } = supabase.storage.from('organization-logos').getPublicUrl(org.logo_url);
          setOrganizationLogo(`${data.publicUrl}?v=${Date.now()}`);
        } else {
          setOrganizationLogo(null);
        }
      }
    } catch (error) {
      console.error('Error fetching user organization:', error);
    }
  };

  const fetchOpenTodosCount = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('todos')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('completed', false);
      if (error) throw error;
      setOpenTodosCount(data?.length || 0);
    } catch (error) {
      console.error('Error fetching open todos count:', error);
    }
  };

  const fetchActiveAreaHotLeadsCount = async () => {
    if (!user) return;
    try {
      // First get the active area ID
      const { data: activeArea } = await supabase
        .from('d2d_areas')
        .select('id')
        .eq('created_by', user.id)
        .eq('is_active', true)
        .single();

      // Also check for active shared areas
      let activeAreaId = activeArea?.id;
      
      if (!activeAreaId) {
        const { data: activeShare } = await supabase
          .from('d2d_area_shares')
          .select('area_id')
          .eq('shared_with', user.id)
          .eq('is_active', true)
          .single();
        
        activeAreaId = activeShare?.area_id;
      }

      if (!activeAreaId) {
        setActiveAreaHotLeadsCount(0);
        return;
      }

      // Get streets in the active area
      const { data: streets } = await supabase
        .from('d2d_streets')
        .select('id')
        .eq('area_id', activeAreaId);

      if (!streets || streets.length === 0) {
        setActiveAreaHotLeadsCount(0);
        return;
      }

      const streetIds = streets.map(s => s.id);

      // Get houses in those streets
      const { data: houses } = await supabase
        .from('d2d_houses')
        .select('id')
        .in('street_id', streetIds);

      if (!houses || houses.length === 0) {
        setActiveAreaHotLeadsCount(0);
        return;
      }

      const houseIds = houses.map(h => h.id);

      // Count HOT leads only in the active area
      const { count } = await supabase
        .from('d2d_units')
        .select('id', { count: 'exact', head: true })
        .in('house_id', houseIds)
        .contains('tags', ['HOT']);

      setActiveAreaHotLeadsCount(count || 0);
    } catch (error) {
      console.error('Error fetching active area hot leads count:', error);
      setActiveAreaHotLeadsCount(0);
    }
  };

  const fetchUnreadEmailsCount = async () => {
    if (!user) return;
    try {
      // First, get the user's active email account
      const { data: emailAccount, error: accountError } = await supabase
        .from('email_accounts')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (accountError || !emailAccount) {
        setUnreadEmailsCount(0);
        return;
      }

      // Fetch total email count from IMAP server
      const { data, error } = await supabase.functions.invoke('get-email-count', {
        body: { accountId: emailAccount.id }
      });

      if (error) {
        console.error('Error fetching email count from server:', error);
        // Fallback to local count
        const { count } = await supabase
          .from('emails')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'received');
        setUnreadEmailsCount(count || 0);
        return;
      }

      setUnreadEmailsCount(data?.total_emails || 0);
    } catch (error) {
      console.error('Error fetching emails count:', error);
      setUnreadEmailsCount(0);
    }
  };

  return {
    userOrganization,
    organizationLogo,
    teamCode,
    isAdmin,
    isTeamLeader,
    openTodosCount,
    activeAreaHotLeadsCount,
    unreadEmailsCount
  };
}
